import React, { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { DndContext, closestCenter } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const DEFAULT_ROLES = [
  'Doctor',
  'Nurse',
  'Independent Prescriber',
  'Paramedic',
  'Student Paramedic',
  'IHCD EMT',
  'FREC 5',
  'FREC 4',
  'FREC 3',
  'First Aid',
  'Event Supervisor',
  'Logistics',
]

const clinicalRoleRanking = [
  'Doctor',
  'Nurse',
  'Independent Prescriber',
  'Paramedic',
  'Student Paramedic',
  'IHCD EMT',
  'FREC 5',
  'FREC 4',
  'FREC 3',
  'First Aid',
]

const normaliseClinicalRoleRanking = (roles = []) => {
  const rankedRoles = roles
    .filter((role) => clinicalRoleRanking.includes(role))
    .sort(
      (a, b) => clinicalRoleRanking.indexOf(a) - clinicalRoleRanking.indexOf(b),
    )
  const unrankedRoles = roles.filter(
    (role) => !clinicalRoleRanking.includes(role),
  )

  return [...rankedRoles, ...unrankedRoles]
}

const isLowestFirstClinicalRanking = (roles = []) => {
  const firstRankedRole = roles.find((role) =>
    clinicalRoleRanking.includes(role),
  )
  const lastRankedRole = roles
    .slice()
    .reverse()
    .find((role) => clinicalRoleRanking.includes(role))

  if (!firstRankedRole || !lastRankedRole) return false

  return (
    clinicalRoleRanking.indexOf(firstRankedRole) >
    clinicalRoleRanking.indexOf(lastRankedRole)
  )
}

const SortableRoleItem = ({ role, index, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: role })

  return (
    <ListItem
      ref={setNodeRef}
      component={Paper}
      variant="outlined"
      sx={{
        mb: 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      secondaryAction={
        <IconButton edge="end" color="error" onClick={() => onDelete(role)}>
          <DeleteIcon />
        </IconButton>
      }
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          mr: 2,
        }}
      >
        <DragIndicatorIcon />
      </Box>

      <ListItemText primary={role} secondary={`Rank ${index + 1}`} />

      {index === 0 && <Chip label="Highest" size="small" />}
    </ListItem>
  )
}

const RoleRankingEditor = ({ roles, setRoles }) => {
  const [newRole, setNewRole] = useState('')
  const hasNormalisedRoles = useRef(false)

  useEffect(() => {
    if (hasNormalisedRoles.current || !isLowestFirstClinicalRanking(roles)) {
      return
    }

    const normalisedRoles = normaliseClinicalRoleRanking(roles)

    if (JSON.stringify(normalisedRoles) !== JSON.stringify(roles)) {
      setRoles(normalisedRoles)
    }

    hasNormalisedRoles.current = true
  }, [roles, setRoles])

  const handleDragEnd = (event) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    const oldIndex = roles.indexOf(active.id)
    const newIndex = roles.indexOf(over.id)

    setRoles(arrayMove(roles, oldIndex, newIndex))
  }

  const addRole = () => {
    const trimmed = newRole.trim()

    if (!trimmed) return
    if (roles.includes(trimmed)) return

    setRoles([...roles, trimmed])
    setNewRole('')
  }

  const deleteRole = (roleToDelete) => {
    setRoles(roles.filter((role) => role !== roleToDelete))
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Clinical Role Ranking
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Drag roles into order from highest to lowest. The system will use this
        order to identify the highest clinical grade assigned to a resource.
      </Typography>

      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          label="New role"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addRole()
            }
          }}
          fullWidth
        />

        <Button variant="contained" onClick={addRole}>
          Add
        </Button>
      </Stack>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={roles}
          strategy={verticalListSortingStrategy}
        >
          <List disablePadding>
            {roles.map((role, index) => (
              <SortableRoleItem
                key={role}
                role={role}
                index={index}
                onDelete={deleteRole}
              />
            ))}
          </List>
        </SortableContext>
      </DndContext>

      <Typography variant="caption" color="text.secondary">
        Top of the list is highest grade. Bottom of the list is lowest grade.
      </Typography>
    </Box>
  )
}

export default RoleRankingEditor
